<template>
  <b-card>
    <validation-observer ref="otherConfigurationRules">
      <b-form>
        <b-row class="mt-2 mb-4">
          <b-col md="4">
            <SelectField
              id="preferred-eod-trigger "
              v-model="eodTriggerType"
              :label="$t('Preferred EOD Trigger')"
              :placeholder="$t('Select')"
              :reduce="option => option.value"
              :options="triggerTypesOptions"
              :error-name="$t('Preferred EOD Trigger')"
              rules="required"
              item="label"
              :readonly="isReadonly"
            />
          </b-col>
          <b-col md="11">
            <TextAreaField
              id="email-to-send-database"
              v-model="eodConfigurationFormData.config.emailsToSendDatabaseBackup"
              :label="$t('Email To Send Database Backup')"
              :placeholder="$t('Enter email to send database backup')"
              for="email-to-send-database"
              rows="3"
              show-icon
              :readonly="isReadonly"
            />
          </b-col>
          <b-col md="1">
            <feather-icon
              icon="InfoIcon"
              size="20"
              class="text-primary"
              @click="onViewModalInfo"
            />
          </b-col>
          <b-col
            md="6"
            class="d-none"
          >
            <SelectField
              id="preferred-eod-sequence "
              v-model="eodConfigurationFormData.config.eodTriggerType"
              :label="$t('Preferred EOD Sequence')"
              :placeholder="$t('Select')"
              :reduce="option => option.value"
              :options="triggerTypesOptions"
              item="label"
              :readonly="isReadonly"
            />
          </b-col>

          <b-col
            md="6"
            class="d-none"
          >
            <SelectField
              id="current-eod-sequence "
              v-model="eodConfigurationFormData.config.eodTriggerType"
              :label="$t('Current EOD Sequence')"
              :placeholder="$t('Select')"
              :reduce="option => option.value"
              :options="triggerTypesOptions"
              item="label"
              :readonly="isReadonly"
            />
          </b-col>
        </b-row>

        <div class="text-center">
          <Button
            v-if="getUserPermission('MODIFY_SYSTEM_SETUP')"
            :label="buttonLabel"
            :loading="isLoading"
            :disabled="isDisabled"
            size="lg"
            @click="saveEODConfiguration(buttonLabel)"
          />
        </div>
      </b-form>
    </validation-observer>
    <b-modal
      id="email-backup-page-info"
      ok-only
      ok-title="Close"
      centered
      :title="$t('INSTRUCTION')"
    >
      <b-card-text>
        {{ $t('Please note that multiple Emails can be separated by comma') }}
      </b-card-text>
    </b-modal>
    <FullScreenSpinner :show="showSpinner" />
  </b-card>
</template>

<script>
import {
  BRow, BCol, BCard, BForm, BCardText, BModal,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import { ValidationObserver } from 'vee-validate'
import { mapActions } from 'vuex'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { required, positive } from '@validations'
import { permissionMixin } from '@core/mixins/permission'
import FullScreenSpinner from '@/views/components/spinner/FullScreenSpinner.vue'
import SelectField from '../../../../forms/SelectField.vue'
import TextAreaField from '../../../../forms/TextAreaField.vue'
import Button from '../../../../components/button/Button.vue'
import { triggerTypes } from '../../../../../helpers/data'

export default {
  components: {
    BRow,
    BCol,
    BCard,
    BForm,
    SelectField,
    BModal,
    ValidationObserver,
    FullScreenSpinner,
    Button,
    BCardText,
    TextAreaField,
  },
  directives: {
    Ripple,
  },
  mixins: [sweetAlert, permissionMixin],
  data() {
    return {
      isReadonly: true,
      isEditable: true,
      isDisabled: false,
      isLoading: false,
      showSpinner: false,
      buttonLabel: this.$t('Edit'),
      required,
      positive,
      eodConfigurationFormData: {
        config: {
          automateEOD: true,
          eodExecutionHour: 0,
          useCurrentDayAsFinancialDate: true,
          emailsToSendDatabaseBackup: '',
        },
      },
      eodTriggerType: '',
      // triggerTypesOptions: triggerTypes,
    }
  },
  computed: {
    triggerTypesOptions() {
      return triggerTypes.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.value }))
    },
  },
  mounted() {
    this.fetchEODConfiguration()
  },
  methods: {
    ...mapActions({
      getEODConfiguration: 'operations/getEODConfigurationList',
      updateEODConfiguration: 'operations/updateEODConfigurationAction',
    }),
    saveEODConfiguration() {
      if (this.buttonLabel === 'Save Changes') {
        this.$refs.otherConfigurationRules.validate().then(success => {
          if (success) {
            this.confirm(this.handleSubmit, { text: this.$t('Are you sure you want to Update this EOD Configurations?') })
          }
        })
      } else {
        this.isReadonly = false
        this.isEditable = true
        this.buttonLabel = this.$t('Save Changes')
      }
    },
    onViewModalInfo() {
      this.$root.$emit('bv::show::modal', 'email-backup-page-info')
    },
    async handleSubmit() {
      try {
        this.isLoading = true
        const eodTriggerType = this.eodTriggerType === 'Automatic'
        const response = await this.updateEODConfiguration({
          config: {
            automateEOD: eodTriggerType,
            eodExecutionHour: 0,
            useCurrentDayAsFinancialDate: true,
            emailsToSendDatabaseBackup: this.eodConfigurationFormData.config.emailsToSendDatabaseBackup,
          },
        })

        const { status } = await response
        if (status === 200) {
          this.fetchEODConfiguration()
          this.alertSuccess({ text: this.$t('Your request to Update EOD Configurations was successful') })
          this.isReadonly = true
          this.isEditable = true
          this.isDisabled = false
          this.isLoading = false
          this.buttonLabel = 'Edit'
        }
      } catch (error) {
        const reasonText = this.$t('Your request to Update EOD Configurations failed. Reason:')
        this.alertError({ text: `${reasonText} ${error}` })
      } finally {
        this.isEditable = true
        this.isDisabled = false
        this.isLoading = false
      }
    },
    async fetchEODConfiguration() {
      try {
        this.showSpinner = true
        const response = await this.getEODConfiguration()
        const { status, data } = response
        if (status === 200 && data.data) {
          const triggerType = data.data.automateEOD === true ? 'Automatic' : 'Managed by User'
          this.eodConfigurationFormData.config = data.data
          this.eodTriggerType = triggerType
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.showSpinner = false
      }
    },
  },
}
</script>
<style scoped>
.custom-checkbox:last-child{
    margin-left: 20px !important;
}
</style>
